import { fetchProjectById, fetchUnitById } from '$lib/queries/cache';
import { normalizeHash, parseHash, projectIdFromRoute, serializeHash, unitIdFromRoute } from './hash';
import { resolveRoute, type ResolvedRoute } from './registry';
import { EMPTY_ENTITY, type Route, type RouteEntity } from './types';

class RouterState {
	hash = $state(typeof location !== 'undefined' ? normalizeHash(location.hash) : '#/');
	unitProjectId = $state<number | null>(null);

	readonly route = $derived(parseHash(this.hash));

	readonly resolved = $derived.by((): ResolvedRoute => resolveRoute(this.hash));

	readonly selectedEntity = $derived.by((): RouteEntity => {
		if (this.route.name === 'unit') {
			return this.route.entity ?? EMPTY_ENTITY;
		}
		return EMPTY_ENTITY;
	});

	readonly projectId = $derived(projectIdFromRoute(this.route, this.unitProjectId));
	readonly unitId = $derived(unitIdFromRoute(this.route));

	navigate(next: Route, opts?: { replace?: boolean }) {
		const nextHash = serializeHash(next);
		if (this.hash === nextHash) return;
		this.hash = nextHash;
		if (opts?.replace) {
			history.replaceState(null, '', nextHash);
		} else {
			history.pushState(null, '', nextHash);
		}
		void this.validateRoute(next);
	}

	private syncFromBrowser() {
		this.hash = normalizeHash(location.hash);
		void this.validateRoute(this.route);
	}

	start() {
		this.syncFromBrowser();
		if (location.hash !== this.hash) {
			history.replaceState(null, '', this.hash);
		}

		const onPopState = () => this.syncFromBrowser();
		window.addEventListener('popstate', onPopState);

		return () => window.removeEventListener('popstate', onPopState);
	}

	async validateRoute(route: Route) {
		if (route.name === 'unit') {
			const unit = await fetchUnitById(route.unitId);
			if (!unit) {
				this.navigate({ name: 'dashboard' }, { replace: true });
				return;
			}
			this.unitProjectId = unit.project;
			return;
		}

		this.unitProjectId = null;

		if (route.name === 'project') {
			const project = await fetchProjectById(route.projectId);
			if (!project) {
				this.navigate({ name: 'dashboard' }, { replace: true });
			}
		}
	}
}

export const router = new RouterState();

export { projectIdFromRoute, unitIdFromRoute };
